"use client";
import React from "react";
import {
  Box,
  Center,
  Divider,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  VStack,
  Text,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import PricingFooter, {TableFooterProps} from "@/components/pricingFooter";
import {useSectionHeadingVariant} from "@/theme/components/useSectionHeading";

type PricingTableData = {
  cpu: string;
  memory: string;
  storage: string;
  bandwidth: string;
  monthlyPrice: number;
};

type PricingTableProps = {
  heading: string;
  data: PricingTableData[];
  footer?: TableFooterProps;
};

export const DesktopView = ({data}: {data: PricingTableData[]}) => {
  return (
    <Box>
      <TableContainer>
        <Table variant="striped" border="2px solid rgb(237,242,247)" width="100%">
          <Thead>
            <Tr>
              <Th textAlign="center" textTransform="none">
                vCPUs
              </Th>
              <Th textAlign="center">Memory</Th>
              <Th textAlign="center">Bandwidth</Th>
              <Th textAlign="center">Storage (NVME)</Th>
              <Th textAlign="center">Monthly Price (USD)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {data.map((item, index) => (
              <Tr key={index}>
                <Td textAlign="center">{item.cpu}</Td>
                <Td textAlign="center">{item.memory}</Td>
                <Td textAlign="center">{item.bandwidth}</Td>
                <Td textAlign="center">{item.storage}</Td>
                <Td
                  textAlign="center"
                  fontWeight="700"
                  fontSize="18px"
                  color="primary.200"
                >
                  ${item.monthlyPrice.toFixed(2)}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const MobileView = ({data}: {data: PricingTableData[]}) => {
  return (
    <SimpleGrid columns={2} spacing={6}>
      {data.map((plan, index) => (
        <VStack
          key={index}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow="md"
          spacing={4}
          w="100%"
          p={4}
          bg="white"
        >
          <Flex direction="column" w="full">
            <Text fontWeight="bold" fontSize="lg">
              {plan.cpu} vCPUs
            </Text>
          </Flex>

          <Divider borderColor="gray.200" />

          <Stack spacing={2} w="full">
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Memory:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.memory}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Storage:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.storage}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Bandwidth:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.bandwidth}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Monthly Price:{" "}
              </Text>
              <Text as="span" fontWeight="bold" fontSize="lg" color="primary.200">
                ${plan.monthlyPrice.toFixed(2)}
              </Text>
            </Box>
          </Stack>
        </VStack>
      ))}
    </SimpleGrid>
  );
};

const VmPricingTable = (props: PricingTableProps) => {
  const {heading, data, footer} = props;
  const HeadingVariant = useSectionHeadingVariant();
  return (
    <Box id="pricing-table-section">
      <Heading variant={HeadingVariant} textAlign="center" mb={8}>
        {heading}
      </Heading>
      <Center display={{md: "none"}} w="full">
        <MobileView data={data} />
      </Center>
      <Box display={{base: "none", md: "block"}}>
        <DesktopView data={data} />
      </Box>
      {footer && <PricingFooter {...footer} />}
      <Divider borderWidth="2px" borderColor="#ECECEC" my={10} />
    </Box>
  );
};

export default VmPricingTable;
